import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DamageType from "components/DamageType/DamageType";
import Select from "react-select";
import GridPaging from "components/Paging/GridPaging";
import SortDirectionIcon from "components/SortDirectionIcon";
import ToolButton from "components/ToolButton/ToolButton";
import "./PestInformation.css";

import {
  getDamageTypes,
  getDataForByStateTable,
  getAllstates,
  getAllYears,
} from "../../services/pestTableService";

const ForestPestByState = () => {
  const [selectedState, setSelectedState] = useState();
  const [selectedType, setSelectedType] = useState();
  const [stateList, setStateList] = useState();
  const [typeList, setTypeList] = useState();
  const [yearList, setYearList] = useState(null);
  const [data, setData] = useState();
  const [currentPageData, setCurrentPageData] = useState();
  const [selectedCol1, setSelectedCol1] = useState();
  const [selectedCol2, setSelectedCol2] = useState();
  const [selectedCol3, setSelectedCol3] = useState();
  const [selectedCol4, setSelectedCol4] = useState();
  const [selectedCol5, setSelectedCol5] = useState();
  const [sortAscending, setSortAscending] = useState(true);
  const [sortedField, setSortedField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const announcement = useRef(null);
  const totalPages = useRef(1);
  const totalRecords = useRef(0);
  const initialLoad = useRef(true);
  const years = useRef();
  const sortedData = useRef();
  const selectState = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  let foundState;
  let foundDamageType;

  useEffect(() => {
    if (!initialLoad.current) {
      setSortedField("State");
      setSortAscending(true);
      setCurrentPage(1);
      getPestSummary();
    }
  }, [selectedState, selectedType]);

  useEffect(() => {
    if (data) {
      sortData(sortedField, sortAscending);
    }
  }, [data]);

  useEffect(() => {
    if (currentPageData) {
      setLoading(false);
    }
  }, [currentPageData]);

  useEffect(() => {
    setLoading(true);
    sliceDataIntoPages();
  }, [pageSize, currentPage]);

  // Initial load
  useEffect(() => {
    announcement.current = "You are now on the By State table page";
    const paramState = searchParams.get("st");
    const paramDamageType = searchParams.get("dt");
    // States
    getAllstates().then((result) => {
      const stateOptions = result.data;
      const allStates = {
        value: 0,
        label: "All States",
        isDisabled: false,
      };
      stateOptions.unshift(allStates);
      const listOfStates = Array.from(stateOptions);
      setStateList(listOfStates);
      if (paramState) {
        initialLoad.current = false;
        foundState = listOfStates.find(
          (element) => element.label === paramState
        );
        if (foundState) {
          setSelectedState(foundState);
        }
      }
    });
    // Damage Types
    getDamageTypes().then((result) => {
      const typesOptions = result.data;
      const listOfDamageTypes = Array.from(typesOptions);
      setTypeList(listOfDamageTypes);
      if (paramDamageType) {
        foundDamageType = listOfDamageTypes.find(
          (element) => element.label === paramDamageType
        );
        if (foundDamageType) {
          setSelectedType(foundDamageType);
        }
      }
    });

    getAllYears().then((result) => {
      years.current = result.data.map((item) => item.value).slice(0, 5);
      setYearList(years.current.slice(0, 5));
      setSelectedCol1(years.current.slice(0, 1));
      setSelectedCol2(years.current.slice(1, 2));
      setSelectedCol3(years.current.slice(2, 3));
      setSelectedCol4(years.current.slice(3, 4));
      setSelectedCol5(years.current.slice(4, 5));
      setSortedField("State");
      getPestSummary();
    });
    initialLoad.current = false;
    document.title = "Forest Pest Conditions - By State";
  }, []);

  const getPestSummary = () => {
    let filter;
    if (foundState) {
      filter = {
        damageTypeId: foundDamageType.value,
        StateFips: foundState.value,
        years: years.current,
      };
    } else {
      filter = {
        damageTypeId: selectedType ? selectedType["value"] : 93,
        StateFips: selectedState ? selectedState["value"] : 0,
        years: years.current,
      };
    }
    getDataForByStateTable(filter).then((result) => {
      totalRecords.current = result.data.data.length;
      setData(result.data.data);
    });
  };

  const formatNumber = (input) => {
    return Number(input).toLocaleString();
  };

  const toggleSort = (field) => {
    let asc = !sortAscending;
    if (!initialLoad.current) {
      setCurrentPage(1);
      if (sortedField === field) {
        setSortAscending((prevState) => !prevState);
        sortData(field, !sortAscending);
      } else {
        asc = true;
        setSortedField(field);
        setSortAscending(true);
        sortData(field, true);
      }
      announcement.current =
        "The table has been sorted by column " +
        field +
        " in " +
        (asc ? "ascending" : "descending") +
        " order";
    }
  };

  const sortData = (field, asc) => {
    sortedData.current = field
      ? [...data].sort((a, b) => {
          const aValue = isNaN(parseFloat(a[field]))
            ? a[field]
            : parseFloat(a[field]);
          const bValue = isNaN(parseFloat(b[field]))
            ? b[field]
            : parseFloat(b[field]);
          if (asc) {
            return aValue < bValue ? -1 : 1;
          } else {
            return aValue > bValue ? -1 : 1;
          }
        })
      : data;
    sliceDataIntoPages();
  };

  const sliceDataIntoPages = () => {
    if (sortedData.current) {
      totalPages.current = Math.ceil(sortedData.current.length / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(
        startIndex + pageSize - 1,
        sortedData.current.length - 1
      );
      if (sortedData.current.length === 0) {
        announcement.current = "No records match your filter selections";
      }
      setCurrentPageData(sortedData.current.slice(startIndex, endIndex + 1));
    }
  };

  //Function to handle page size
  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  //Function to handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  function handlePrint() {
    const lblState = selectedState != null ? selectedState.label : "All States";
    const lblDamageType =
      selectedType != null ? selectedType.label : "All Damage Types";
    const table = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write("<html><head><title>By State</title>");
    printWindow.document.write("<style>");
    printWindow.document.write(
      "table {border-collapse: collapse; width: 100%;}  td, th { border:0.5px solid black; text-align:left; } th{background-color: #f0f0f0;} tr:nth-child(odd) { background-color: #edf1e6;} "
    );

    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      "<h2>By State</h2>" +
        "<h3>" +
        lblState +
        " - " +
        lblDamageType +
        " (acres) </h3>"
    );
    printWindow.document.write(table);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  }
  //Copy to clipboard
  function handleCopy() {
    const tableHTML = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(tableHTML);
      }
    });
  }
  //Share the link
  function handleShare() {
    let url = window.location.href;
    if (url.indexOf("?") > -1) {
      url = url.substring(0, url.indexOf("?"));
    }
    const damageType =
      selectedType != null ? selectedType.label : "All Damage Types";
    const State = selectedState != null ? selectedState.label : "All States";
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(
          url +
            "?dt=" +
            encodeURIComponent(damageType) +
            "&st=" +
            encodeURIComponent(State)
        );
      }
    });
  }

  return (
    <main className="grid-container pest-information" id="main-content">
      <h1 className="main-heading">By State</h1>
      <p className="pest-information-intro">
        The default view includes all damage types and biotic agents only,
        combined as damage displayed in acres. Abiotic agents such as drought
        and frost are excluded. Data are derived from the authoritative Forest
        Health Protection{" "}
        <a
          href="https://www.fs.usda.gov/science-technology/data-tools-products/fhp-mapping-reporting/detection-surveys"
          target="_blank"
          className="usa-link"
          rel="noreferrer"
        >
          Insect and Disease Survey
        </a>{" "}
        database. To filter by state, select from the options below.{" "}
      </p>
      <DamageType />
      <br />
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-5 mobile-lg:grid-col-12">
          <span className="label_text">Select State</span>
          {
            <Select
              ref={selectState}
              className="react-select"
              placeholder="All States"
              aria-label="Select a state. Press the down arrow to expand the option list. Press delete
              to clear the current selection."
              name="singleSelect"
              value={selectedState}
              onChange={(value) => setSelectedState(value)}
              options={stateList}
              hideselectedoptions="true"
              isClearable={true}
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#1b1b1b",
                }),
              }}
            />
          }
        </div>
        <div className="tablet:grid-col-4 mobile-lg:grid-col-12">
          <span className="label_text">Select Damage Type</span>
          {
            <Select
              className="react-select"
              placeholder="All Damage Types"
              aria-label="Select a damage type. Press the down arrow to expand the option list. Press delete
              to clear the current selection."
              name="singleSelect"
              value={selectedType}
              onChange={(value) => setSelectedType(value)}
              options={typeList}
              hideselectedoptions="true"
              isClearable={true}
              styles={{
                placeholder: (baseStyles) => ({
                  ...baseStyles,
                  color: "#1b1b1b",
                }),
              }}
            />
          }
        </div>
      </div>
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-9 mobile-lg:grid-col-12">
          <span className="show-records_label">Show </span>
          <select
            className="paging_select"
            value={pageSize}
            onChange={handleChangePageSize}
            name="singleSelect"
            placeholder="All"
            aria-label="All"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value={data?.length}>All</option>
          </select>
          <span className="show-records_label"> Records</span>
        </div>
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <ToolButton
            currentPage={currentPage}
            handlePrint={handlePrint}
            handleCopy={handleCopy}
            handleShare={handleShare}
          ></ToolButton>
        </div>
      </div>
      <div className="visually-hidden" aria-live="polite" aria-busy={loading}>
        {announcement.current}
      </div>
      <div className="grid-col-12 usa-table-container--scrollable">
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div>
            {currentPageData && currentPageData.length > 0 ? (
              <table
                aria-label="Pest Information by State"
                id="bystatetable"
                className="usa-table"
              >
                <thead>
                  <tr>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("State")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("State");
                      }}
                      scope="col"
                      aria-label="State (sortable)"
                    >
                      STATE
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "State"}
                      />
                    </th>
                    {yearList?.map((year) => (
                      <th
                        tabIndex="0"
                        key={year}
                        onClick={() => toggleSort(year)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleSort(year);
                        }}
                        scope="col"
                        aria-label={year + " acres (sortable)"}
                      >
                        {year}
                        {" (acres)"}
                        <SortDirectionIcon
                          asc={sortAscending}
                          visible={sortedField === year}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((item, index) => (
                    <tr key={item["State"]} id={item["State"]}>
                      <td>{item["State"]}</td>
                      <td>{formatNumber(item[selectedCol1])}</td>
                      <td>{formatNumber(item[selectedCol2])}</td>
                      <td>{formatNumber(item[selectedCol3])}</td>
                      <td>{formatNumber(item[selectedCol4])}</td>
                      <td>{formatNumber(item[selectedCol5])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="label_text">
                No records match your filter selections
              </p>
            )}
          </div>
        )}
      </div>
      <GridPaging
        currentPage={currentPage}
        totalPages={totalPages.current}
        handlePageChange={handlePageChange}
        pageSize={pageSize}
        totalRecords={totalRecords.current}
      />
      {sortedData.current && (
        <div
          id="hidden-table-for-copy-and-print"
          className="grid-col-12 usa-table-container--scrollable"
          aria-hidden="true"
        >
          <table>
            <thead>
              <tr>
                <th>STATE</th>
                {yearList?.map((year) => (
                  <th key={year}>
                    {year}
                    {" (acres)"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.current.map((item, index) => (
                <tr key={item["State"]}>
                  <td>{item["State"]}</td>
                  <td>{formatNumber(item[selectedCol1])}</td>
                  <td>{formatNumber(item[selectedCol2])}</td>
                  <td>{formatNumber(item[selectedCol3])}</td>
                  <td>{formatNumber(item[selectedCol4])}</td>
                  <td>{formatNumber(item[selectedCol5])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default ForestPestByState;
